import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { getCheckoutStatus } from '../lib/api';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, ArrowRight, MapPin } from 'lucide-react';
import { toast } from 'sonner';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await getCheckoutStatus(sessionId);
      
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Payment successful! Your listing is now active.');
        return;
      } else if (res.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts((prev) => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-24 noise-bg">
      <div className="w-full max-w-md text-center">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <MapPin className="w-6 h-6 text-primary-foreground" />
          </div>
          <span className="text-2xl font-semibold">
            <span className="text-foreground">Local</span>
            <span className="text-primary">Edge</span>
          </span>
        </Link>

        <div className="glass-card rounded-2xl border border-border p-8">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
              <h1 className="text-2xl font-semibold mb-2">Processing Payment</h1>
              <p className="text-muted-foreground mb-6">
                Please wait while we confirm your payment...
              </p>
              <div className="text-sm text-muted-foreground">
                Attempt {attempts + 1} of 10
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-500/20 mx-auto mb-6 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h1 className="text-2xl font-semibold mb-2">Payment Successful!</h1>
              <p className="text-muted-foreground mb-8">
                Your subscription is now active. Let's set up your business listing!
              </p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full h-12 rounded-xl gap-2"
                data-testid="goto-dashboard"
              >
                Go to Dashboard
                <ArrowRight className="w-5 h-5" />
              </Button>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="w-16 h-16 rounded-full bg-destructive/20 mx-auto mb-6 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-destructive" />
              </div>
              <h1 className="text-2xl font-semibold mb-2">Session Expired</h1>
              <p className="text-muted-foreground mb-8">
                Your payment session has expired. Please try again.
              </p>
              <Button
                onClick={() => navigate('/pricing')}
                className="w-full h-12 rounded-xl"
              >
                Try Again
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-full bg-destructive/20 mx-auto mb-6 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-destructive" />
              </div>
              <h1 className="text-2xl font-semibold mb-2">Something Went Wrong</h1>
              <p className="text-muted-foreground mb-8">
                We couldn't verify your payment. Please contact support if this persists.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/pricing')}
                  className="w-full h-12 rounded-xl"
                >
                  Try Again
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="w-full h-12 rounded-xl"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}

          {status === 'timeout' && (
            <>
              <div className="w-16 h-16 rounded-full bg-yellow-500/20 mx-auto mb-6 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-yellow-500" />
              </div>
              <h1 className="text-2xl font-semibold mb-2">Taking Longer Than Expected</h1>
              <p className="text-muted-foreground mb-8">
                Your payment may still be processing. Check your dashboard or email for confirmation.
              </p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full h-12 rounded-xl"
              >
                Check Dashboard
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
